import dayjs from 'dayjs';

import {i18n} from '@yourcoach/i18n';

import type {PaymentExpandedCommon} from '../../api/payment';

import {cardStore} from '../../api/payment';
import {currentUserStore} from '../../api/user';
import {datetimeObjToISOString} from '../../utils/datetime';
import {getCourseLabel} from '../../utils/entity/course';
import {getFileSrc} from '../../utils/entity/file';
import {isMeCoachForEntity} from '../../utils/entity/isMeCoachForEntity';
import {Currency, getPriceString} from '../../utils/money';
import {getAvailableAmountToRefund} from './stores/MakeRefund/utils';
import {MIN_REFUND_AMOUNT} from './utils';

export class PaymentModel {
  payment: PaymentExpandedCommon;

  constructor(payment: PaymentExpandedCommon) {
    this.payment = payment;
  }

  get availableAmountToRefund() {
    return getAvailableAmountToRefund(this.payment);
  }

  get course() {
    return this.payment.course;
  }

  get courseLabel() {
    return getCourseLabel(this.course);
  }

  get currency() {
    return Currency[this.payment.currency];
  }

  get dateString() {
    return dayjs(datetimeObjToISOString(this.dateTimeObj)).format('lll');
  }

  get dateTimeObj() {
    if (this.transferToMe?.transferred) {
      return this.transferToMe.transferred;
    } else if (this.payment.paid) {
      return this.payment.paid;
    } else if (this.isFailedOrFrozen) {
      return this.payment.end_date;
    }

    return this.payment.start_date;
  }

  get hasRefunds() {
    return Boolean(this.refunds.length);
  }

  get isFailedOrFrozen() {
    return this.status === 'failed' || this.status === 'frozen';
  }

  get isPartiallyRefunded() {
    return (
      this.payment.refunds.some(refund => refund?.status === 'complete') &&
      this.availableAmountToRefund.byCard > MIN_REFUND_AMOUNT
    );
  }

  get isRefundAvailable() {
    return (
      this.shouldShowRefunds &&
      this.availableAmountToRefund.byCard > MIN_REFUND_AMOUNT
    );
  }

  get isSucceeded() {
    return this.status === 'complete' || this.status === 'paid';
  }

  get paymentMethod() {
    return this.payment.card_id ? cardStore.cards[this.payment.card_id] : null;
  }

  get processing() {
    return this.payment.processing;
  }

  get programAvatar() {
    return getFileSrc(this.payment.program.avatar).url;
  }

  get programTitle() {
    return this.payment.program.title;
  }

  get refundedAmount() {
    return this.payment.refunded_amount;
  }

  get refundedAmountString() {
    return this.refundedAmount
      ? getPriceString({
          currency: this.currency,
          price: this.refundedAmount,
        })
      : '';
  }

  get refunds() {
    return this.payment.refunds.filter(Boolean);
  }

  get shouldShowRefunds() {
    return Boolean(
      isMeCoachForEntity(this.payment) &&
        this.processing === 'internal' &&
        this.isSucceeded,
    );
  }

  get status() {
    return this.payment.status;
  }

  get statusString() {
    return this.isFailedOrFrozen
      ? i18n.t('shared:failed')
      : this.isSucceeded
        ? i18n.t('shared:success')
        : i18n.t('shared:pending');
  }

  get tax() {
    // tax is difference of the payment amount and a sum of all transfers
    const transfersAmount = Object.values(this.payment.transfers).reduce(
      (acc, transfer) => {
        return acc + transfer.amount;
      },
      0,
    );

    return Math.max(this.totalAmount - transfersAmount, 0);
  }

  get taxString() {
    return getPriceString({
      currency: this.currency,
      intZeroPrice: true,
      price: this.tax,
    });
  }

  get toJS() {
    return this.payment;
  }

  get totalAmount() {
    return this.payment.amount;
  }

  get totalAmountString() {
    return getPriceString({
      currency: this.currency,
      price: this.totalAmount,
    });
  }

  get transferToMe() {
    return currentUserStore.user
      ? this.payment.transfers[currentUserStore.user._id]
      : null;
  }

  get transferToMeString() {
    return this.transferToMe
      ? getPriceString({
          currency: this.currency,
          price: this.transferToMe.amount,
        })
      : '';
  }

  get userName() {
    return this.payment.user.name;
  }
}
